import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, mergeMap, of, throwError } from 'rxjs';
import { UserAccountApiService, type AdminProfileData } from './user-account-api.service';

/** Generic silhouette placeholder when no profile photo is stored (bundled SVG). */
export const DEFAULT_ADMIN_AVATAR = '/assets/images/default-profile-avatar.svg';

export interface AdminProfileSnapshot {
  displayName: string;
  email: string;
  avatarUrl: string;
}

function avatarUrlFromProfilePicture(profilePicture: AdminProfileData['profile_picture']): string {
  const previewUrl =
    profilePicture &&
    typeof profilePicture === 'object' &&
    !Array.isArray(profilePicture)
      ? (profilePicture as { preview_url?: string }).preview_url?.trim()
      : undefined;
  if (previewUrl) {
    return previewUrl;
  }
  return DEFAULT_ADMIN_AVATAR;
}

function snapshotFromProfile(data: AdminProfileData): AdminProfileSnapshot {
  return {
    displayName: (data.full_name ?? '').trim() || 'User',
    email: (data.email_id ?? '').trim(),
    avatarUrl: avatarUrlFromProfilePicture(data.profile_picture),
  };
}

/**
 * Holds the signed-in admin's display name, email, and avatar for the shell (header).
 * Populated by {@link loadProfile} (session guard + optional explicit refresh).
 */
@Injectable({ providedIn: 'root' })
export class AdminSessionProfileService {
  private readonly snap = new BehaviorSubject<AdminProfileSnapshot>({
    displayName: 'User',
    email: '',
    avatarUrl: DEFAULT_ADMIN_AVATAR,
  });

  private lastProfile: AdminProfileData | null = null;

  readonly snapshot$: Observable<AdminProfileSnapshot> = this.snap.asObservable();

  constructor(private readonly userApi: UserAccountApiService) {}

  getSnapshot(): AdminProfileSnapshot {
    return this.snap.getValue();
  }

  /** Last successful profile payload (includes phone fields for forms). */
  getLastProfile(): AdminProfileData | null {
    return this.lastProfile;
  }

  /** Session guard / sign-in redirect — loads profile or fails if unauthenticated. */
  loadProfile(): Observable<AdminProfileSnapshot> {
    return this.userApi.getAdminProfile().pipe(
      mergeMap((env) => {
        if (
          env?.status !== true ||
          (typeof env.statusCode === 'number' && env.statusCode >= 400)
        ) {
          return throwError(() => new Error('Not authenticated'));
        }
        const data = env.data;
        if (!data) {
          return throwError(() => new Error('Not authenticated'));
        }
        this.applyFromProfileData(data);
        return of(this.snap.getValue());
      }),
    );
  }

  applyFromProfileData(data: AdminProfileData): void {
    this.lastProfile = data;
    this.snap.next(snapshotFromProfile(data));
  }

  /** Reset header profile after session expiry / sign-out. */
  clearSession(): void {
    this.lastProfile = null;
    this.snap.next({
      displayName: 'User',
      email: '',
      avatarUrl: DEFAULT_ADMIN_AVATAR,
    });
  }

  parseHttpError(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object') {
        const msg = (body as { message?: unknown }).message;
        if (typeof msg === 'string') {
          return msg;
        }
        if (Array.isArray(msg)) {
          return msg.map(String).join(' ');
        }
      }
      if (err.status === 401) {
        return 'Your session expired. Please sign in again.';
      }
      if (err.status === 0) {
        return 'Cannot reach the API. Check that the backend is running.';
      }
    }
    return fallback;
  }
}
